import { throttle } from 'lodash';
import { matchPath } from 'react-router';
import { ROUTE_ADMIN } from 'src/config/routes';

export function flattenDeepObjectKeys(obj: Record<string, any> | any): string[] {
    const isObject = (val: unknown) => typeof val === 'object' && !Array.isArray(val);

    const addDelimiter = (a: string, b: string) => (a ? `${a}.${b}` : b);

    const paths = (obj: object = {}, head: string = ''): string[] => {
        return Object.entries(obj).reduce((product: string[], [key, value]) => {
            const fullPath = addDelimiter(head, key);
            return isObject(value) ? product.concat(paths(value, fullPath)) : product.concat(fullPath);
        }, []);
    };

    return paths(obj);
}

export function maybeNum(val: string | number): string | number {
    const num = typeof val === 'string' ? parseInt(val) : val;
    return isNaN(num) ? val : num;
}

export function convertSerializedPathOfToArray(path: SerializedPathOf<any> | string): (string | number)[] {
    return (path as string).split('.').map(x => maybeNum(x));
}

export function convertArrayToPathName(paths: (string | number)[] | PathOf<any>): string {
    return (paths as (string | number)[])
        .map(x => maybeNum(x))
        .reduce((previous, path: string | number, index): string => {
            return `${previous}${typeof path === 'string' ? `${index > 0 ? '.' : ''}${path}` : `[${path}]`}`;
        }, '')
        .toString();
}

/**
 * Creates an array of the given type, by using the constructor on each element in a JSON array.
 */
export function fromJsonArray<T>(constructor: new (json: any) => T, json: any): T[] {
    return json != null && Array.isArray(json) ? json.map((element: any) => new constructor(element)) : [];
}

/**
 * Creates an array of the given type, by using the given provider on each element in a JSON array.
 */
export function fromJsonArrayWith<T>(provider: (json: any) => T | undefined, json: any): T[] {
    return json != null && Array.isArray(json) ? json.map((element: any) => provider(element)!) : [];
}

/**
 * Throttles an async function in a way that can be awaited.
 * By default throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to throttle calls for.
 * @param wait same function as lodash.throttle's wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export function asyncThrottle<F extends (...args: any[]) => Promise<any>>(func: F, wait?: number) {
    const throttled = throttle((resolve, reject, args: Parameters<F>) => {
        func(...args)
            .then(resolve)
            .catch(reject);
    }, wait);
    return (...args: Parameters<F>): ReturnType<F> =>
        new Promise((resolve, reject) => {
            throttled(resolve, reject, args);
        }) as ReturnType<F>;
}

/**
 * Given an array of numbers and a target selects the option from the
 * array closest to the target.
 *
 * @param options The possible options
 * @param target  The target to compare against
 * @returns The closes value in options to target or undefined
 */
export function selectClosestTo(options: number[], target: number): number | undefined {
    let bestMatch: number | undefined;
    for (const option of options) {
        if (bestMatch === undefined) {
            bestMatch = option;
            continue;
        }
        if (target - option < target - bestMatch) {
            bestMatch = option;
        }
    }
    return bestMatch;
}

export function isAdminContext(locationPath: string): boolean {
    return !!matchPath(locationPath, {
        path: ROUTE_ADMIN.path,
        exact: false,
    });
}
